import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { formatInt, isValidNumericInput, useMainDeclarationCalculation } from '../utils/declarationUtils';

const DeclarationDataTable = ({ declaration, isEditing, draft, onDraftChange, errorFields }) => {
  const { t } = useTranslation();

  const derivedValues = useMainDeclarationCalculation(draft, isEditing);

  const renderAmount = value => <span className="m-table-obvious-inner">{formatInt(value)}</span>;

  const handleChange = field => event => {
    if (!onDraftChange) return;
    const value = event.target.value;
    if (!isValidNumericInput(value)) return;
    onDraftChange(field, value);
  };

  const renderEditable = field => (
    <input
      className={`declaration-input${errorFields && errorFields.has(field) ? ' declaration-input--error' : ''}`}
      value={(draft && draft[field]) || ''}
      onChange={handleChange(field)}
      inputMode="numeric"
      maxLength={15}
    />
  );

  const renderValue = (field, computedFallback) => {
    if (!isEditing) return renderAmount(declaration[field]);
    if (computedFallback !== undefined) return renderAmount(computedFallback);
    return renderEditable(field);
  };

  const UNIT_PERSON = t('features.ReportIncomeTax.show.main_form.unit_person');

  const rows = [
    { no: '1', field: 'ct16TotalEmployees', code: '16', unit: UNIT_PERSON, rowSpan: 2 },
    { field: 'ct17ResidentEmployees', code: '17', unit: UNIT_PERSON },
    { no: '2', field: 'ct18TotalWithTax', code: '18', unit: UNIT_PERSON, computed: derivedValues.ct18TotalWithTax },
    { no: '2.1', field: 'ct19ResidentWithTax', code: '19', unit: UNIT_PERSON },
    { no: '2.2', field: 'ct20NonResidentWithTax', code: '20', unit: UNIT_PERSON },
    { no: '3', field: 'ct21TotalTaxableIncome', code: '21', unit: 'VND', computed: derivedValues.ct21TotalTaxableIncome },
    { no: '3.1', field: 'ct22ResidentTaxableIncome', code: '22', unit: 'VND' },
    { no: '3.2', field: 'ct23NonResidentTaxableIncome', code: '23', unit: 'VND' },
    { no: '3.3', field: 'ct24InsurancePremium', code: '24', unit: 'VND' },
    { no: '4', field: 'ct25OilContractExemption', code: '25', unit: 'VND' },
    { no: '5', field: 'ct26TotalTaxableSubjectToWithholding', code: '26', unit: 'VND', computed: derivedValues.ct26TotalTaxableSubjectToWithholding },
    { no: '5.1', field: 'ct27ResidentTaxableSubjectToWithholding', code: '27', unit: 'VND' },
    { no: '5.2', field: 'ct28NonResidentTaxableSubjectToWithholding', code: '28', unit: 'VND' },
    { no: '6', field: 'ct29TotalTaxWithheld', code: '29', unit: 'VND', computed: derivedValues.ct29TotalTaxWithheld },
    { no: '6.1', field: 'ct30ResidentTax', code: '30', unit: 'VND' },
    { no: '6.2', field: 'ct31NonResidentTax', code: '31', unit: 'VND', className: 'indent-1' },
    { no: '6.3', field: 'ct32InsuranceTax', code: '32', unit: 'VND', className: 'indent-1', computed: derivedValues.ct32InsuranceTax }
  ];

  return (
    <div className="declaration-form-section">
      <div className="declaration-form-section__body">
        <table className="declaration-data-table">
          <thead>
            <tr>
              <th className="col-no">{t('features.ReportIncomeTax.show.main_form.column_no')}</th>
              <th>{t('features.ReportIncomeTax.show.main_form.table.description')}</th>
              <th className="col-code">{t('features.ReportIncomeTax.show.main_form.table.code')}</th>
              <th className="col-code">{t('features.ReportIncomeTax.show.main_form.table.unit')}</th>
              <th className="col-amount">{t('features.ReportIncomeTax.show.main_form.table.amount')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.code} className={row.className}>
                {row.no !== undefined ? (
                  <td className="col-no" rowSpan={row.rowSpan}>
                    {row.no}
                  </td>
                ) : null}
                <td>{t(`features.ReportIncomeTax.show.main_form.ct${row.code}_desc`)}</td>
                <td className="col-code">[{row.code}]</td>
                <td className="col-code">{row.unit}</td>
                <td className="col-amount">{renderValue(row.field, row.computed)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

DeclarationDataTable.propTypes = {
  declaration: PropTypes.shape({
    ct16TotalEmployees: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct17ResidentEmployees: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct18TotalWithTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct19ResidentWithTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct20NonResidentWithTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct21TotalTaxableIncome: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct22ResidentTaxableIncome: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct23NonResidentTaxableIncome: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct24InsurancePremium: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct25OilContractExemption: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct26TotalTaxableSubjectToWithholding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct27ResidentTaxableSubjectToWithholding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct28NonResidentTaxableSubjectToWithholding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct29TotalTaxWithheld: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct30ResidentTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct31NonResidentTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct32InsuranceTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }).isRequired,
  isEditing: PropTypes.bool,
  draft: PropTypes.shape({
    ct16TotalEmployees: PropTypes.string,
    ct17ResidentEmployees: PropTypes.string,
    ct19ResidentWithTax: PropTypes.string,
    ct20NonResidentWithTax: PropTypes.string,
    ct22ResidentTaxableIncome: PropTypes.string,
    ct23NonResidentTaxableIncome: PropTypes.string,
    ct24InsurancePremium: PropTypes.string,
    ct25OilContractExemption: PropTypes.string,
    ct27ResidentTaxableSubjectToWithholding: PropTypes.string,
    ct28NonResidentTaxableSubjectToWithholding: PropTypes.string,
    ct30ResidentTax: PropTypes.string,
    ct31NonResidentTax: PropTypes.string
  }),
  onDraftChange: PropTypes.func,
  errorFields: PropTypes.instanceOf(Set)
};

DeclarationDataTable.defaultProps = {
  isEditing: false,
  draft: null,
  onDraftChange: null,
  errorFields: null
};

export default DeclarationDataTable;
